import * as api from 'helpers/api';
import pluralize from 'pluralize';

import taskApi from 'components/tasks/api';
import * as connectionsApi from 'components/connections/api';

import {
  UNEXPECTED_ERROR_MESSAGE,
} from './messages';
import debounceActionCreators from 'helpers/redux/debounce_action_creators';
import { cloneDeep } from 'lodash';

const debouncedActionCreators = debounceActionCreators({ toggleAlert }, 5000);

export const ADD_TASK = 'add_task';
export const ADD_TASKS = 'add_tasks';
export const FETCH_TASKS = 'fetch_tasks';
export const UPDATE_TASK = 'update_task';
export const REMOVE_TASK = 'remove_task';
export const ADD_LEAD = 'add_lead';
export const FETCH_LEADS = 'fetch_leads';
export const FETCH_LEADS_LOADING = 'fetch_leads_loading';
export const SET_LEADS_META = 'set_leads_meta';
export const REMOVE_LEAD = 'remove_lead';
export const UPDATE_LEAD = 'update_lead';
export const UPDATE_LEADS = 'update_leads';
export const APPROVE_LEAD_VAULT_ACCESS = 'approve_lead_vault_access';
export const REVOKE_LEAD_VAULT_ACCESS = 'revoke_lead_vault_access';
export const BLOCK_LEAD_VAULT_ACCESS = 'block_lead_vault_access';
export const UNBLOCK_LEAD_VAULT_ACCESS = 'unblock_lead_vault_access';
export const TOGGLE_EMAIL_MODAL = 'toggle_email_modal';
export const TOGGLE_TASK_MODAL = 'toggle_task_modal';
export const SET_TASK_MODAL_ERRORS = 'set_task_modal_erorrs';
export const TOGGLE_LEAD_MODAL = 'toggle_lead_modal';
export const TOGGLE_LEAD_MODAL_ALERT = 'toggle_lead_modal_alert';
export const CLOSE_MODAL_SNACKBAR = 'close_modal_snackbar';
export const MODAL_REQUEST_FINISHED = 'modal_request_finished';
export const MODAL_REQUEST_SENDING = 'modal_request_sending';
export const TOGGLE_SELECT_LEAD = 'toggle_select_lead';
export const DESELECT_LEAD = 'deselect_lead';
export const TOGGLE_ALERT = 'toggle_alert';
export const FETCH_CONNECTABLE_OBJECTS = 'fetch_connectable_objects';
export const UPDATE_CONNECTABLE_OBJECT = 'update_connectable_object';
export const UPDATE_CONNECTABLE_OBJECTS = 'update_connectable_objects';
export const TOGGLE_PAGE_FLAG = 'toggle_page_flag';
export const SELECT_SHADOW = 'SELECT_SHADOW';
export const SET_SHADOW_DIRTY = 'SET_SHADOW_DIRTY';
export const FETCH_DEALS = 'FETCH_DEALS';
export const HIDE_ACTION_INDICATOR = 'HIDE_ACTION_INDICATOR';
export const SHOW_ACTION_INDICATOR = 'SHOW_ACTION_INDICATOR';

export function fetchTasks() {
  return (dispatch, getState) => {
    api.get(`/projects/${getState().project.id}/tasks`).then((response) => {
      dispatch({ type: FETCH_TASKS, payload: response.tasks });
    });
  };
}

export function addTask(taskData, onSuccess, onError) {
  return (dispatch) => {
    taskApi.addTask(taskData).then((response) => {
      dispatch({ type: ADD_TASK, payload: response });
      dispatch(hideTaskModal());
      dispatch(showTempAlert(response.logged ? 'Activity successfully logged.' : 'Task successfully created.', false));
      if (onSuccess)  onSuccess(response);
    }).catch((errors) => {
      dispatch(setTaskModalErrors(errors));
      if (onError)  onError(errors);
    });
  };
}

export function updateTask(task, onSuccess, onError) {
  return (dispatch) => {
    taskApi.updateTask(task).then((response) => {
      dispatch({ type: UPDATE_TASK, payload: response });
      dispatch(hideTaskModal());
      if (onSuccess)  onSuccess(response);
    }).catch((errors) => {
      dispatch(setTaskModalErrors(errors));
      if (onError)  onError(errors);
    });
  };
}

export function createBulkTask(task, contacts) {
  return (dispatch, getState) => {
    taskApi.bulkCreate(task, contacts, getState().project.id).then((response) => {
      dispatch({ type: ADD_TASKS, payload: response.tasks });
      dispatch(showTempAlert(response.message));
    });
  };
}

export function createCallListWithTasks(data, ids) {
  return (dispatch, getState) => {
    const { callListName, ...taskData } = data;
    const callList = {
      name: callListName,
      assigneeId: taskData.assigneeId
    };

    return taskApi.createCallList(
      callList,
      taskData,
      ids,
      getState().project.id
    ).then((response) => {
      dispatch({ type: ADD_TASKS, payload: response.tasks });
      dispatch(showTempAlert(response.message));
    });
  };
}

export function toggleTaskCompleted(task) {
  const newTask = { ...task };
  newTask.completed = !task.completed;
  return (dispatch) => {
    taskApi.updateTask(newTask).then((task) => {
      dispatch({
        type: UPDATE_TASK,
        payload: task
      });
    });
  };
}

export function removeTask(task) {
  return (dispatch) => {
    taskApi.removeTask(task).then(() => {
      dispatch({
        type: REMOVE_TASK,
        payload: task.id
      });
    });
  };
}

export function clearErrors() {
  return dispatch => dispatch(hideLeadModalAlert());
}

export function addLead(projectId, contact, leadData = {}) {
  leadData = {
    ...leadData,
    contactId: contact.associatedModelId,
    contactType: contact.associatedModelType,
    newContact: contact.newContact
  };

  const request = api.post(`/projects/${projectId}/leads`, { lead: leadData });

  return (dispatch) => {
    request.then((response) => {
      dispatch({
        type: ADD_LEAD,
        payload: response
      });

      dispatch(hideLeadModal(true, 'Created'));
    }).catch((errors) => {
      if (errors) dispatch(showLeadModalAlert(errors[0]));
      dispatch({ type: MODAL_REQUEST_FINISHED });
    });
  };
}

// Module-level counter so we can ignore stale responses when the user changes
// filters/sort/page mid-flight. Without this, the in-flight fetch would resolve
// last and overwrite the table with results that don't match the active filters.
let activeLeadsFetchId = 0;

export function fetchLeads(projectId, queryParams = {}) {
  return (dispatch) => {
    const fetchId = ++activeLeadsFetchId;
    dispatch({ type: FETCH_LEADS_LOADING });

    return api.get(`/projects/${projectId}/leads.json`, { params: queryParams })
      .then(({ leads, meta }) => {
        if (fetchId !== activeLeadsFetchId) return; // a newer fetch is in flight; drop stale result
        dispatch({ type: FETCH_LEADS, payload: leads });
        dispatch({ type: SET_LEADS_META, payload: meta });
      })
      .catch(() => {
        if (fetchId !== activeLeadsFetchId) return; // newer fetch will set state
        // Reset to a non-fetching empty state so the UI doesn't get stuck on the spinner,
        // and surface a toast so the user knows something went wrong.
        dispatch({ type: FETCH_LEADS, payload: [] });
        dispatch({ type: SET_LEADS_META, payload: null });
        dispatch(toggleAlert(UNEXPECTED_ERROR_MESSAGE, true));
      });
  };
}

export function fetchMatches(projectId, params) {
  return api.get(`/projects/${projectId}/matches.json`, { params });
}

export function removeLead(projectId, lead, successCallback, errorCallback) {
  const request = api.deleteRequest(`/projects/${projectId}/leads/${lead.id}`);

  return (dispatch) => {
    request.then(() => {
      dispatch({ type: REMOVE_LEAD, payload: lead.id });
      dispatch({ type: DESELECT_LEAD, payload: lead.id });

      if (successCallback) successCallback();
    }).catch((response) => {
      if (errorCallback) errorCallback(response.response.errors);
    });
  };
}

export function bulkRemoveLeads(projectId, leadIds) {
  const request = api.deleteRequest(`/projects/${projectId}/leads/bulk_delete_leads/`, { data: { lead_ids: leadIds } });

  return (dispatch) => {
    return request.then(() => {
      leadIds.forEach((leadId) => {
        dispatch({
          type: REMOVE_LEAD,
          payload: leadId
        });
      });
      dispatch(showTempAlert(`Successfully removed ${pluralize('lead', leadIds.length, true)}`));
      dispatch(changeSelectedLeads([]));
    });
  };
}

export function updateLead(projectId, lead) {
  const { id, ...remaining } = lead;

  const leadData = cloneDeep(remaining);
  if (leadData.contactAttributes && leadData.contactAttributes.address) {
    leadData.contactAttributes.addressAttributes = leadData.contactAttributes.address;
    delete leadData.contactAttributes.address;
  }

  const request = api.put(`/projects/${projectId}/leads/${id}`, { lead: leadData });

  return (dispatch) => {
    request.then((response) => {
      dispatch({
        type: UPDATE_LEAD,
        payload: response
      });

      dispatch(hideLeadModal(true, 'Updated'));
    }).catch((errors) => {
      if (errors) dispatch(showLeadModalAlert(errors[0]));
    }).finally(() => {
      dispatch(modalRequestFinished());
    });
  };
}

export function updateLeadNote(projectId, lead) {
  const { id, noteToClient } = lead;

  const request = api.put(`/projects/${projectId}/leads/${id}`, {
    noteToClientAttributes: { body: noteToClient.body }
  });

  return (dispatch) => {
    return request.then((response) => {
      dispatch({
        type: UPDATE_LEAD,
        payload: response
      });
    }).catch((error) => {
      throw error;
    });
  };
}

export function approveLeadVaultAccess(leadId, propertyVaultProfileId, approveAccessPath) {
  const request = api.patch(approveAccessPath);

  return (dispatch) => {
    return request.then(() => {
      dispatch({
        type: APPROVE_LEAD_VAULT_ACCESS,
        payload: {
          updatedLeadId: leadId,
          propertyVaultProfileId
        }
      });
    }).catch(() => {
      dispatch(showTempAlert(UNEXPECTED_ERROR_MESSAGE, true));
    });
  };
}

export function revokeLeadVaultAccess(leadId, propertyVaultProfileId, revokeAccessPath) {
  const request = api.patch(revokeAccessPath);

  return (dispatch) => {
    return request.then(() => {
      dispatch({
        type: REVOKE_LEAD_VAULT_ACCESS,
        payload: {
          updatedLeadId: leadId,
          propertyVaultProfileId
        }
      });
    }).catch(() => {
      dispatch(showTempAlert(UNEXPECTED_ERROR_MESSAGE, true));
    });
  };
}

export function blockLeadVaultAccess(leadId, projectId) {
  const payload = {
    projectId: projectId,
    transition: 'block'
  };
  const request =
    api.put(`/sharing_leads/${leadId}`, payload);
  return (dispatch) => {
    return request.then(() => {
      dispatch({
        type: BLOCK_LEAD_VAULT_ACCESS,
        payload: {
          updatedLeadId: leadId
        }
      });
    }).catch(() => {
      dispatch(showTempAlert(UNEXPECTED_ERROR_MESSAGE, true));
    });
  };
}

export function unblockLeadVaultAccess(leadId, projectId) {
  const payload = {
    projectId: projectId,
    transition: 'unblock'
  };
  const request =
    api.put(`/sharing_leads/${leadId}`, payload);

  return (dispatch) => {
    return request.then(() => {
      dispatch({
        type: UNBLOCK_LEAD_VAULT_ACCESS,
        payload: {
          updatedLeadId: leadId
        }
      });
    }).catch(() => {
      dispatch(showTempAlert(UNEXPECTED_ERROR_MESSAGE, true));
    });
  };
}

export function toggleSelectAllLeads() {
  return (dispatch, getState) => {
    const leads = getState().leads;
    const selectedLeads = getState().selectedLeads || [];

    const allSelected = leads.every(lead => selectedLeads.map(l => l[0]).indexOf(lead.id) > -1);

    if (allSelected) {
      dispatch(changeSelectedLeads([]));
    } else {
      dispatch(changeSelectedLeads(leads.map(lead => [lead.id, lead.contactType])));
    }
  };
}

export function toggleSelectLead(leadId, contactType) {
  return (dispatch, getState) => {
    const selectedLeads = getState().selectedLeads || [];
    let newSelected;

    if (selectedLeads.map(l => l[0]).indexOf(leadId) > -1) {
      newSelected = selectedLeads.filter(selectedLeads => selectedLeads[0] !== leadId);
    } else {
      newSelected = [...selectedLeads, [leadId, contactType]];
    }

    dispatch(changeSelectedLeads(newSelected));
  };
}

export function connectEntities(databaseEntityType, databaseEntityId, projectEntityType, projectEntityId) {
  return (dispatch) => {
    return (
      connectionsApi
        .connect(databaseEntityType, databaseEntityId, projectEntityType, projectEntityId)
        .then((updatedConnectableObject) => {
          dispatch({
            type: UPDATE_CONNECTABLE_OBJECT,
            payload: updatedConnectableObject
          });
        })
    );
  };
}

export function bulkCopyAndConnect(databaseId, projectEntities) {
  return (dispatch) => {
    return connectionsApi.bulkCopyAndConnect(databaseId, projectEntities)
      .then(({ objects, leads }) => {
        if (leads.length > 0) {
          dispatch({ type: UPDATE_LEADS, payload: leads });
          dispatch(showTempAlert(`Successfully added ${pluralize('leads', leads.length, true)} to database.`));
        }

        return dispatch({ type: UPDATE_CONNECTABLE_OBJECTS, payload: objects });
      });
  };
}

export function copyAndConnect(databaseId, projectEntityType, projectEntityId) {
  return (dispatch) => {
    return connectionsApi.copyAndConnect(databaseId, projectEntityType, projectEntityId)
      .then(({ object, lead }) => {
        if (lead) {
          dispatch({ type: UPDATE_LEAD, payload: lead });
          dispatch(showTempAlert('Successfully added lead to database.'));
        }

        return dispatch({ type: UPDATE_CONNECTABLE_OBJECT, payload: object });
      });
  };
}

export function fetchConnectableObjects(projectId) {
  return (dispatch) => {
    return api.get(`/projects/${projectId}/connectable_objects`).then((response) => {
      dispatch({
        type: FETCH_CONNECTABLE_OBJECTS,
        payload: response
      });
    });
  };
}

export function unlink(linkId, projectEntityType, projectEntityId) {
  return (dispatch) => {
    return (
      connectionsApi
        .unlink(linkId, projectEntityType, projectEntityId)
        .then((updatedConnectableObject) => {
          dispatch({
            type: UPDATE_CONNECTABLE_OBJECT,
            payload: updatedConnectableObject
          });
        })
    );
  };
}

export function toggleAlert(message, error) {
  return { message, error, type: TOGGLE_ALERT };
}

function delayedHideAlert() {
  return dispatch => debouncedActionCreators.toggleAlert(dispatch, '', false);
}

export function showTempAlert(message, error) {
  return (dispatch) => {
    dispatch(toggleAlert(message, error));
    dispatch(delayedHideAlert());
  };
}

export function changeSelectedLeads(leads) {
  return ({ type: TOGGLE_SELECT_LEAD, payload: { leads } });
}

export function hideTaskModal() {
  return ({ type: TOGGLE_TASK_MODAL, payload: { show: false, taskToEdit: null, isNote: false } });
}

export function showEditTaskModal(taskToEdit) {
  return ({ type: TOGGLE_TASK_MODAL, payload: { show: true, taskToEdit, isNote: false } });
}

export function closeModalSnackbar() {
  return dispatch => dispatch({ type: CLOSE_MODAL_SNACKBAR });
}

export function modalRequestSending() {
  return dispatch => dispatch({ type: MODAL_REQUEST_SENDING });
}

export function modalRequestFinished() {
  return dispatch => dispatch({ type: MODAL_REQUEST_FINISHED });
}

export function showLogTaskModal({ defaultTaskAssociation } = {}) {
  return ({
    type: TOGGLE_TASK_MODAL,
    payload: {
      show: true,
      taskToEdit: null,
      isNote: false,
      isLogged: true,
      defaultTaskAssociation
    }
  });
}

export function showNewTaskModal({ defaultTaskAssociation } = {}) {
  return {
    type: TOGGLE_TASK_MODAL,
    payload: {
      show: true,
      taskToEdit: null,
      isNote: false,
      isLogged: false,
      defaultTaskAssociation
    }
  };
}

export function setTaskModalErrors(errors) {
  return { type: SET_TASK_MODAL_ERRORS, errors };
}

export function showEditNoteModal(taskToEdit) {
  return ({ type: TOGGLE_TASK_MODAL, payload: { show: true, taskToEdit, isNote: true } });
}

export function showNewNoteModal() {
  return ({ type: TOGGLE_TASK_MODAL, payload: { show: true, taskToEdit: null, isNote: true, isLogged: true } });
}

export function showNewLeadModal() {
  return ({ type: TOGGLE_LEAD_MODAL, payload: { show: true, leadToEdit: null } });
}

export function hideLeadModal(showSnackbar = false, snackbarActionText = '') {
  return ({ type: TOGGLE_LEAD_MODAL, payload: { show: false, leadToEdit: null, showSnackbar, snackbarActionText } });
}

export function showEmailModal(emails) {
  return ({ type: TOGGLE_EMAIL_MODAL, payload: { show: true, emails } });
}

export function hideEmailModal() {
  return ({ type: TOGGLE_EMAIL_MODAL, payload: { show: false, emails: null } });
}

export function showUpdateLeadModal(lead) {
  return ({ type: TOGGLE_LEAD_MODAL, payload: { show: true, leadToEdit: lead } });
}

function showLeadModalAlert(errorMessage) {
  return ({ type: TOGGLE_LEAD_MODAL_ALERT, showAlert: true, errorMessage });
}

export function hideLeadModalAlert() {
  return ({ type: TOGGLE_LEAD_MODAL_ALERT, showAlert: false });
}

export function togglePageFlag(page) {
  return ({ type: TOGGLE_PAGE_FLAG, page });
}

export function selectShadow(shadow) {
  return ({ type: SELECT_SHADOW, shadow });
}

export function setShadowDirty(isDirty) {
  return ({ type: SET_SHADOW_DIRTY, isDirty });
}

export function fetchDeals(dealsPath) {
  return (dispatch) => {
    api.get(dealsPath).then((response) => {
      dispatch({ type: FETCH_DEALS, payload: response });
    });
  };
}

export function hideActionIndicator(key) {
  return ({ type: HIDE_ACTION_INDICATOR, key });
}

export function showActionIndicator(key) {
  return ({ type: SHOW_ACTION_INDICATOR, key });
}
